<template>
  <div id="spraying-screen" class="h-100 d-flex flex-column flex-1" v-if="farm">
    <template v-if="!!rosMap && rosMap.scene.scaleX === 1">
      <div
        style="z-index: 99; left: 0"
        class="loader position-fixed top-0 start-0 bg-white h-100 w-100 opacity-90 d-flex justify-content-center align-items-center"
      >
        <div class="d-flex align-items-center">
          <i class="material-icons">hourglass_empty</i>
          <strong>{{$t('global.loading')}}</strong>
        </div>
      </div>
    </template>
    <div class="spraying-main-commands py-3 d-flex" v-if="!displayGoToRowMessage">
      <b-button-group class="mr-1">
        <b-button
          v-if="SprayerRoverStatus === 'Stop'"
          variant="success"
          :pressed="null"
          class="d-flex justify-content-center align-items-center"
          @click="commandToRover('Go')"
        >
          <i class="material-icons">play_arrow</i>
        </b-button>
        <b-button
          :pressed="null"
          v-if="SprayerRoverStatus === 'Go'"
          variant="light"
          class="d-flex justify-content-center align-items-center start-rover-action"
          @click="commandToRover('Stop')"
        >
          <i class="material-icons">stop</i>
        </b-button>
      </b-button-group>
      <b-button-group class="mr-1 flex-1">
        <b-button
          :pressed="null"
          variant="light"
          class="d-flex justify-content-center align-items-center"
          @click="handleGoHome()"
        >
          <i class="material-icons">home</i>
        </b-button>
        <b-button
          :pressed="null"
          variant="light"
          class="d-flex justify-content-center align-items-center"
          @click="displayGoToRowMessage = true"
        >
          <i class="material-icons">menu_open</i>
        </b-button>

        <b-button
          variant="light"
          class="d-flex justify-content-center align-items-center"
          @click="handleRefill()"
        >
          <i class="material-icons">opacity</i>
        </b-button>
      </b-button-group>
      <b-button-group>
        <b-button
          :href="'/#/spraying-settings/' + id"
          variant="dark"
          class="d-flex justify-content-center align-items-center"
        >
          <i class="material-icons">settings</i>
        </b-button>
      </b-button-group>
    </div>
    <div
      class="alert alert-light p-2 my-3 d-flex justify-content-between align-items-center"
      v-if="displayGoToRowMessage"
    >
      <span class="text-dark">
        <strong>{{ $t('spraying.clickOnSpotMessage')}}</strong>
      </span>
      <b-button
        variant="link"
        class="text-dark"
        @click="displayGoToRowMessage = false"
      >{{ $t('global.cancel')}}</b-button>
    </div>
    <div class="spraying-rate py-3" v-if="SprayendPercentage > 0 && SprayerRoverStatus === 'Go'">
      <div class="spraying-rate-eta d-flex justify-content-between align-items-center">
        <span>Spraying... (Rate {{ Number(SprayRate) }} l/Ha)</span>
        <span>
          Eta:
          <strong>{{ Number(SprayedEta) }}m</strong>
        </span>
      </div>
      <b-progress class="my-2" :max="100" show-progress variant="success">
        <b-progress-bar :value="SprayendPercentage">
          <span>{{Number(SprayendPercentage)}}% ({{Number(SprayedRows)}} Row)</span>
        </b-progress-bar>
      </b-progress>
    </div>
    <div class="spraying-map min-h-100 position-relative">
      <i
        v-if="!!rosMap && !!farm.home"
        class="material-icons flare"
        :style="{ left: rosToGlobal(rosMap, {x: farm.home[0], y: farm.home[1]}).x - 12 + 'px', top: rosToGlobal(rosMap, {x: farm.home[0], y: farm.home[1]}).y - 10 + 'px' }"
      >home</i>
      <template v-if="!!mergedSides && !!rosMap">
        <div
          v-for="key in Object.keys(mergedSides)"
          :style="{ left: rosToGlobal(rosMap, {x: mergedSides[key][0], y: mergedSides[key][1]}).x - 12 + 'px', top: rosToGlobal(rosMap, {x: mergedSides[key][0], y: mergedSides[key][1]}).y - 10 + 'px' }"
          :key="key"
          class="flare"
        >{{key}}</div>
      </template>
      <Map v-on:coordinates="onMapTouched" name="spraying" :farmId="farm.id" @initMap="initMap" />
    </div>
    <div class="spraying-other-actions">
      <div class="d-flex flex-nowrap w-100">
        <div id="spraying-tank" class="flex-fill px-2 spraying bg-white p-4">
          <span class="font-weight-bold text-dark">{{ $t('spraying.tank')}}</span>
          <b-badge
            variant="danger"
            class="d-flex align-items-center justify-content-start"
            v-if="SprayTankError"
          >
            <span class="material-icons mr-1">report_gmailerrorred</span>
            {{ $t('spraying.errorTank')}}
          </b-badge>
        </div>

        <div
          id="spraying-pump"
          class="flex-fill px-2 text-left bg-white p-4 border border-top-0 border-bottom-0"
        >
          <span class="font-weight-bold mb-1" v-if="!SprayPumpError">{{ $t('spraying.pump')}}</span>
          <b-badge
            variant="danger"
            class="d-flex align-items-center justify-content-start mb-1"
            v-if="SprayPumpError"
          >
            <span class="material-icons mr-1">report_gmailerrorred</span>
            {{ $t('global.error')}} {{ $t('spraying.pump')}}
          </b-badge>
          <div class="d-flex flex-nowrap">
            <b-button
              size="sm"
              variant="light"
              @click="startOrStopSprayingMotor(true)"
              class="flex-fill"
            >{{ $t('global.open')}}</b-button>
            <b-button
              size="sm"
              variant="light"
              @click="startOrStopSprayingMotor(false)"
              class="flex-fill"
            >{{ $t('global.close')}}</b-button>
          </div>
        </div>
        <div id="spraying-valve" class="flex-fill px-2 text-left bg-white p-4">
          <span class="font-weight-bold mb-1" v-if="!SprayValveError">{{ $t('spraying.valve')}}</span>
          <b-badge
            variant="danger"
            class="d-flex align-items-center justify-content-start mb-1"
            v-if="SprayValveError"
          >
            <span class="material-icons mr-1">report_gmailerrorred</span>
            {{ $t('global.error')}} {{ $t('spraying.valve')}}
          </b-badge>
          <div class="d-flex flex-nowrap">
            <b-button
              size="sm"
              variant="light"
              @click="startStopSprayingValve(true)"
              class="flex-fill"
            >{{ $t('global.open')}}</b-button>
            <b-button
              size="sm"
              variant="light"
              @click="startStopSprayingValve(false)"
              class="flex-fill"
            >{{ $t('global.close')}}</b-button>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="modal-refill" ref="modal-refill" centered hide-header>
      <div>
        <p>
          <strong>{{ $t('modal.refillTitle')}}</strong>
        </p>
        <p>{{ $t('modal.refillDescription')}}</p>
      </div>
      <template #modal-footer>
        <b-button
          variant="link"
          class="text-uppercase text-dark"
          @click="$root.$emit('bv::hide::modal', 'modal-refill', $event.target)"
        >{{ $t('global.cancel')}}</b-button>
        <b-button
          variant="link"
          class="text-uppercase text-dark"
          @click="commandToRover('Refill')"
        >{{ $t('spraying.refill')}}</b-button>
      </template>
    </b-modal>
    <b-modal ref="modal-go-home" id="modal-go-home" centered hide-header>
      <div>
        <p>
          <strong>{{ $t('modal.goHomeTitle')}}</strong>
        </p>
        <p>{{ $t('modal.goHomeDescription')}}</p>
      </div>
      <template #modal-footer>
        <b-button
          variant="link"
          class="text-uppercase text-dark"
          @click="$root.$emit('bv::hide::modal', 'modal-go-home', $event.target)"
        >{{ $t('global.cancel')}}</b-button>
        <b-button
          variant="link"
          class="text-uppercase text-dark"
          @click="commandToRover('Home')"
        >{{ $t('spraying.goBackHome')}}</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import RosService from "@/services/RosService.js";
import Map from "@/components/Map.vue";

export default {
  name: "Spraying",
  props: {
    id: {
      required: true
    }
  },
  components: {
    Map
  },
  data() {
    return {
      farm: null,
      displayGoToRowMessage: false,
      rosMap: null,
      mergedSides: null
    };
  },
  computed: {
    ...mapState({
      SprayerRoverStatus: state => state.chat.sprayingState.SprayerRoverStatus,
      SprayedRows: state => state.chat.sprayingState.SprayedRows,
      SprayedEta: state => state.chat.sprayingState.SprayedEta,
      SprayendPercentage: state => state.chat.sprayingState.SprayendPercentage,
      SprayRate: state => state.chat.sprayingState.SprayRate,
      SprayTankError: state => state.chat.sprayingState.SprayTankError,
      SprayPumpError: state => state.chat.sprayingState.SprayPumpError,
      SprayValveError: state => state.chat.sprayingState.SprayValveError,
      confirmationRequired: state => state.confirmationRequired,
      farms: state => state.farms.all
    }),
    showHome() {
      return this.farm?.homeX != null && this.farm?.homeY != null;
    },
    showFlare() {
      return this.gotoX != null && this.gotoX != null;
    }
  },
  created() {
    if (this.farms) {
      this.farm = this.farms.find(farm => farm.id === parseInt(this.id));
      if (!!this.farm.entries) {
        this.mergedSides = {
          ...this.farm.entries.side1,
          ...this.farm.entries.side2
        };
      }
    }
  },
  methods: {
    initMap(viewer) {
      this.rosMap = viewer;
    },
    handleGoHome() {
      if (this.confirmationRequired) {
        this.$refs["modal-go-home"].show();
        return;
      }
      this.commandToRover("Home");
    },
    handleRefill() {
      if (this.confirmationRequired) {
        this.$refs["modal-refill"].show();
        return;
      }
      this.commandToRover("Refill");
    },
    onMapTouched(coordinates) {
      const closest = this.getClosestPoint(coordinates);
      if (closest) {
        this.commandToRover("Resume " + closest);
      }
      this.displayGoToRowMessage = false;
    },

    commandToRover(command) {
      this.$refs["modal-go-home"].hide();
      this.$refs["modal-refill"].hide();
      RosService.sendRoverSprayingCommand(command);
    },

    startStopSprayingValve(command) {
      if (
        !this.confirmationRequired ||
        confirm(this.$t("global.confirmation_question"))
      ) {
        RosService.startStopSprayingValve(command);
      }
    },
    startOrStopSprayingMotor(command) {
      if (
        !this.confirmationRequired ||
        confirm(this.$t("global.confirmation_question"))
      ) {
        RosService.startOrStopSprayingMotor(command);
      }
    },
    toPixelsX(x) {
      return Math.floor((x * 350) / 100);
    },
    toPixelsY(y) {
      return Math.floor((y * 500) / 100);
    },
    getClosestPoint(coordinates) {
      if (!this.mergedSides) {
        return;
      }

      let closest = null;
      let distance = Infinity;

      for (let key of Object.keys(this.mergedSides)) {
        const convertedCoord = this.rosToGlobal(this.rosMap, {
          x: this.mergedSides[key][0],
          y: this.mergedSides[key][1]
        });
        console.log(convertedCoord, coordinates);
        let d = Math.sqrt(
          (coordinates.x - convertedCoord.x) ** 2 +
            (coordinates.y - convertedCoord.y) ** 2
        );

        if (d < distance) {
          closest = key;
          distance = d;
        }
      }
      return closest;
    },
    globalToRos(viewer, x, y) {
      var rosX = (x - viewer.scene.x) / viewer.scene.scaleX;
      var rosY = (viewer.scene.y - y) / viewer.scene.scaleY;
      return new ROSLIB.Vector3({
        x: rosX,
        y: rosY
      });
    },
    rosToGlobal(viewer, pos) {
      var x = pos.x * viewer.scene.scaleX + viewer.scene.x;
      var y = -(viewer.scene.scaleY * pos.y - viewer.scene.y); // Flip Y coordinates
      return {
        x: x,
        y: y
      };
    }
  }
};
</script>

<style scoped>
.flare {
  position: absolute;
  z-index: 10;
  height: 20px;
  width: 20px;
  background-color: red;
  color: #fff;
  text-align: center;
  line-height: 1.8;
  font-size: 12px;
  border-radius: 50px;
  left: 0;
  top: 0;
}
</style>
